import { AsyncPipe, CurrencyPipe, NgTemplateOutlet } from '@angular/common'
import {
    Component,
    OnInit,
    TemplateRef,
    ViewChild,
    computed,
    effect,
    inject,
    input,
    numberAttribute,
    viewChild,
} from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { NgbModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap'
import { distinctUntilChanged, map, of } from 'rxjs'
import { SmartQuoteService } from 'src/app/core/services/smartquote/smartquote.service'
import { CustomerDetailComponent } from 'src/app/shared/components/customer/customer-detail/customer-detail.component'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { SmartQuoteLineItem } from 'src/app/shared/interfaces/smartquote/smart-quote-line.interface'
import { SmartQuote } from 'src/app/shared/interfaces/smartquote/smart-quote.interface'
import { SmartquoteHelperService } from '../services/smartquote-helper.service'
import { SmartQuoteBasketComponent } from './components/smartquote-basket/smartquote-basket.component'
import { ToastrService } from 'ngx-toastr'
import { CanDeactivateComponent } from 'src/app/core/guards/unsaved-changes.guard'
import { SmartquoteQuoteComponent } from './components/smartquote-quote/smartquote-quote.component'
import { AppStore } from 'src/app/core/app.store'
import { AppointmentCardComponent } from 'src/app/shared/components/appointments/appointment-card/appointment-card.component'
import { SmartquoteJobDetailsComponent } from './components/smartquote-job-details/smartquote-job-details.component'
import { SmartquoteCustomerDetailsComponent } from './components/smartquote-customer-details/smartquote-customer-details.component'
import { SmartquoteTotalsComponent } from './components/smartquote-totals/smartquote-totals.component'
import { WolseleyService } from 'src/app/core/services/wolseley/wolseley.service'
import { SmartQuoteLineForm } from '../helpers/smartquote-line-form.interface'
import { SmartQuoteForm } from '../helpers/smartquote-form.interface'
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service'
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { environment } from 'src/environments/environment'
import { SmartquoteTopBarComponent } from './components/smartquote-top-bar/smartquote-top-bar.component'
import { OrdersTableComponent } from 'src/app/shared/components/orders/orders-table/orders-table.component'
import { PageActionBarComponent } from 'src/app/shared/components/page-action-bar/page-action-bar.component'
import { ConditionalRequiredValidator } from 'src/app/shared/validators/conditionalRequired.validator'
import { WolseleyCtaComponent } from 'src/app/shared/components/adverts/wolseley-cta/wolseley-cta.component'
import { CardComponent } from 'src/app/shared/components/card/card.component'
import { SmartquoteDetailsComponent } from './components/smartquote-details/smartquote-details.component'
import { SmartquoteTopBar2Component } from './components/smartquote-top-bar-2/smartquote-top-bar-2.component'
import { SmartquoteSummaryComponent } from './components/smartquote-summary/smartquote-summary.component'
import { ApplianceCardComponent } from 'src/app/shared/components/appliances/appliance-card/appliance-card.component'

@Component({
    selector: 'sf-smartquote',
    imports: [
        NgTemplateOutlet,
        NgbNavModule,
        NgxPermissionsModule,
        FontAwesomeModule,
        ButtonComponent,
        PageActionBarComponent,
        CustomerDetailComponent,
        ApplianceCardComponent,
        CardComponent,
        ButtonComponent,
        SmartquoteTopBarComponent,
        SmartquoteCustomerDetailsComponent,
        SmartquoteJobDetailsComponent,
        SmartQuoteBasketComponent,
        SmartquoteQuoteComponent,
        SmartquoteTotalsComponent,
        SmartquoteDetailsComponent,
        SmartquoteTopBar2Component,
        SmartquoteSummaryComponent,
        AppointmentCardComponent,
        OrdersTableComponent,
        WolseleyCtaComponent,
        AsyncPipe,
        CurrencyPipe,
    ],
    templateUrl: './smartquote-edit.component.html',
    styleUrls: ['./smartquote-edit.component.scss'],
    providers: [SmartquoteHelperService],
})
export class SmartquoteEditComponent extends CanDeactivateComponent implements OnInit {
    #fb = inject(FormBuilder)
    #ngxPermissionsService = inject(NgxPermissionsService)
    #route = inject(ActivatedRoute)
    #toastr = inject(ToastrService)
    #wolseleyService = inject(WolseleyService)
    #modalService = inject(NgbModal)
    #smartquoteHelperService = inject(SmartquoteHelperService)

    smartQuoteTemplates = this.#smartquoteHelperService.smartQuoteTemplates
    companySettings = this.#smartquoteHelperService.companySettings
    customer = this.#smartquoteHelperService.customer
    mainAppliance = this.#smartquoteHelperService.mainAppliance
    appointment = this.#smartquoteHelperService.appointment
    quote = this.#smartquoteHelperService.smartquote
    quoteAccepted = computed(() => this.#smartquoteHelperService.smartquote()?.accepted || false)
    quoteDeclined = computed(() => this.#smartquoteHelperService.smartquote()?.declined || false)
    orderPlaced = computed(() => this.#smartquoteHelperService.smartquote()?.hasOrderBeenPlaced || false)
    workAddress = computed(() => {
        const quote = this.quote()
        if (quote?.workAddress) {
            return quote.workAddress
        }
        const items = [quote?.addressLine1, quote?.addressLine2, quote?.town, quote?.county, quote?.postcode]
        return items.filter(Boolean).join(', ')
    })
    orders = this.#smartquoteHelperService.orders
    statistics = this.#smartquoteHelperService.statistics
    landingPageUrl = computed(() => {
        const smartQuote = this.quote()
        const customer = this.customer()
        const companySettings = this.#smartquoteHelperService.companySettings()

        if (smartQuote === null || customer === null || companySettings === null) {
            return null
        }

        return `${environment.legacyUrl}public/smartquoteaccept/${companySettings.companyId}/${smartQuote.id}/${customer.apiKey}`
    })

    awaitingorderModal = viewChild<TemplateRef<any>>('awaitingorderModal')

    #appStore = inject(AppStore)

    markup = computed(() => {
        const markup = this.#appStore.companySettings()?.smartQuotePartsMarkup

        return markup ? markup / 100 + 1 : 1
    })

    form: FormGroup = this.#fb.group<SmartQuoteForm>(
        {
            customerId: this.#fb.control(null, [Validators.required]),
            emailAddress: this.#fb.nonNullable.control('', [Validators.required, Validators.email]),
            customerName: this.#fb.nonNullable.control('', [Validators.required]),
            addressLine1: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            addressLine2: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            town: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            county: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            postcode: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            workAddress: this.#fb.nonNullable.control('', [Validators.required, Validators.maxLength(1000)]),
            notes: this.#fb.nonNullable.control('', [Validators.maxLength(2000)]),
            engineerNotes: this.#fb.nonNullable.control('', [Validators.maxLength(2000)]),
            staffNotes: this.#fb.nonNullable.control('', [Validators.maxLength(2000)]),
            labourRate: this.#fb.nonNullable.control(0),
            labourCost: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            labourHours: this.#fb.nonNullable.control(0),
            partsCost: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            deposit: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            depositPercentage: this.#fb.nonNullable.control(0),
            discount: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            discountPercentage: this.#fb.nonNullable.control(0),
            moneyOff: this.#fb.nonNullable.control(0),
            vatMultiplier: this.#fb.nonNullable.control(1.2, [Validators.required]),
            vatTotal: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            combinedLines: this.#fb.nonNullable.control(false),
            freeOfCharge: this.#fb.nonNullable.control(false),
            accepted: this.#fb.nonNullable.control(false),
            declined: this.#fb.nonNullable.control(false),
            defaultImageUrl: this.#fb.control(null, [Validators.maxLength(500)]),
            lineItems: this.#fb.array<SmartQuoteLineForm>([]),
        },
        {
            validators: [
                ConditionalRequiredValidator('workAddress', '', 'addressLine1', true),
                ConditionalRequiredValidator('workAddress', '', 'postcode', true),
                ConditionalRequiredValidator('workAddress', '', 'workAddress', false),
            ],
        }
    )

    get lineItems(): FormArray<SmartQuoteLineForm> {
        return this.form.get('lineItems') as FormArray
    }

    get doAllLineItemsHaveCatalogNumbers(): boolean {
        const result =
            !!this.lineItems.length &&
            this.lineItems.value.every((x) => x.catalogNumber !== null && x.catalogNumber !== undefined)
        return result
    }

    catalogCodes$ = this.form.valueChanges.pipe(
        map((x) => x.lineItems),
        map((x) =>
            x
                .map((l: SmartQuoteLineItem) => ({ productCode: l.catalogNumber, quantity: l.quantity }))
                .filter((p: { productCode: null | undefined }) => p.productCode !== null && p.productCode !== undefined)
        )
    )

    customerId = input(null, { transform: numberAttribute })
    smartquoteId = input(null, { transform: numberAttribute })

    constructor() {
        super()

        effect(() => {
            const companySettings = this.#smartquoteHelperService.companySettings()

            if (companySettings && !this.quote()) {
                this.form.patchValue({
                    combinedLines: companySettings.smartQuoteCombinedLines,
                    labourRate: companySettings.engineerCostPerHour,
                })
            }
        })
    }

    smartQuoteEffect = effect(() => {
        const smartquote = this.quote()
        if (smartquote === null) {
            this.form.reset()
            return
        }

        debugger

        if (smartquote.accepted && !smartquote.hasOrderBeenPlaced) {
            this.#modalService.open(this.awaitingorderModal(), { centered: true, backdrop: 'static' })
        }

        this.form.patchValue(smartquote)
        smartquote.lineItems.forEach((lineItem) => {
            this.lineItems.push(this.mapLineItem(lineItem))
        })
    })

    customerEffect = effect(() => {
        const customer = this.customer()

        if (!customer || this.quote()) {
            return
        }

        this.form.patchValue({
            customerId: customer.id,
            customerName: customer.fullName,
            emailAddress: customer.emailAddress,
        })

        if (this.smartquoteId() && !customer.postcode) {
            this.form.patchValue({
                workAddress: customer.fullAddressReturnCarriage,
            })
        } else {
            this.form.patchValue({
                addressLine1: customer.addressLine1,
                addressLine2: customer.addressLine2,
                town: customer.town,
                county: customer.county,
                postcode: customer.postcode,
            })
        }
    })

    ngOnInit(): void {
        this.#smartquoteHelperService.customerId.set(this.customerId())
        this.#smartquoteHelperService.smartQuoteId.set(this.smartquoteId())

        this.form.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
            this.#smartquoteHelperService.updateStats(value as SmartQuote)
            const statistics = this.#smartquoteHelperService.statistics()

            const controlsToUpdate: Record<string, number> = {
                deposit: statistics.depositTotal,
                labourCost: statistics.labourCost,
                partsCost: statistics.linesTotal,
                discount: statistics.discountAsPercentage,
                vatTotal: statistics.totalVat,
            }

            Object.keys(controlsToUpdate).forEach((controlName) => {
                this.form.controls[controlName].patchValue(controlsToUpdate[controlName], {
                    emitEvent: false,
                    onlySelf: true,
                })
            })
        })
    }

    mapLineItem(item: SmartQuoteLineItem): FormGroup {
        return this.#fb.group({
            id: [item.id],
            catalogNumber: [item.catalogNumber],
            productId: [item.productId],
            description: [item.description],
            imageUrl: [item.imageUrl],
            mpn: [item.mpn],
            quantity: [item.quantity, [Validators.min(1)]],
            cost: [item.cost, [Validators.required]],
            terms: [item.terms],
        })
    }

    saveSmartQuote() {
        this.form.markAsDirty()
        this.form.markAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        this.form.markAsPristine()
        this.form.markAsUntouched()

        const smartQuote = this.form.getRawValue() as SmartQuote

        if (!this.quote()) {
            this.#smartquoteHelperService.createSmartquote(smartQuote)
        } else {
            this.#smartquoteHelperService.updateSmartquote(smartQuote)
        }
    }

    acceptQuote(): void {
        this.form.markAsDirty()
        this.form.markAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        this.#smartquoteHelperService.markQuoteAsAccepted()
    }

    declineQuote(): void {
        this.form.markAsDirty()
        this.form.markAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        this.#smartquoteHelperService.markQuoteAsDeclined()
    }

    deleteQuote(): void {
        this.#smartquoteHelperService.deleteSmartQuote()
    }

    copyQuote(): void {
        this.#smartquoteHelperService.copySmartquote()
    }

    async loadWolseleyTerms(): Promise<void> {
        const hasWolseley = await this.#ngxPermissionsService.hasPermission('Wolseley')

        if (!hasWolseley) {
            return
        }

        const productCodes = this.lineItems.value
            ?.filter(
                (x) => x.catalogNumber !== null && x.catalogNumber !== undefined && (x.terms === 0 || x.terms === null)
            )
            .map((x) => x.catalogNumber!)

        this.#wolseleyService.getWolseleyPrices(productCodes).subscribe((prices) => {
            this.lineItems.controls.forEach((control) => {
                const lineItem = control.value
                const price = prices.products.productPrices.find((x) => x.productCode === lineItem.catalogNumber)

                if (price) {
                    control.patchValue({ terms: price.sellingPrice }, { emitEvent: true })
                }
            })
        })
    }

    setDefaultImageUrl(imageUrl: string): void {
        this.form.patchValue({ defaultImageUrl: imageUrl })
    }

    public override canDeactivate(): boolean {
        return !(this.form.dirty && this.form.touched)
    }
}
